import React, { useState, useEffect } from "react";
import { Image, Button, Icon, Checkbox, Input } from "semantic-ui-react";
import { map, forEach } from "lodash";
import { useParams, useHistory } from "react-router-dom";
import { useOrder, useTable } from "../../../hooks";
import {
  removeProductCartApi,
  cleanProductCartApi,
  updateProductCartApi,
} from "../../../api/cart";
import { DISCOUNT_REASONS } from "../../../utils/constants";
import "./ListProductCart.scss";

export function ListProductCart(props) {
  const {
    products,
    onReloadCart,
    idTable: idTableProp,
    redirectPath,
    defaultTakeaway = false,
  } = props;
  const [total, setTotal] = useState(0);
  // Carrito del mozo (Admin, llega idTable): por línea, "Para llevar",
  // "Cortesía" con motivo (como Restaurant.pe) y "Nota para cocina" (el mozo
  // agrega con "+" sin abrir el modal de personalizar, que es del comensal).
  // El comensal no ve nada de esto.
  const staffMode = !!idTableProp;
  const [staffOpts, setStaffOpts] = useState({});
  useEffect(() => {
    setStaffOpts(
      Object.fromEntries(
        (products || []).map((product, index) => [
          index,
          {
            // Sin marca del mozo (undefined) → el valor de la mesa; si ya lo
            // encendió o apagó a mano, se respeta.
            takeaway: product.takeaway ?? defaultTakeaway,
            courtesy: !!product.courtesy,
            courtesyReason: product.courtesyReason || "",
            note: product.note || "",
          },
        ])
      )
    );
  }, [products, defaultTakeaway]);
  const setOpt = (index, patch) => {
    setStaffOpts((prev) => ({ ...prev, [index]: { ...prev[index], ...patch } }));
    updateProductCartApi(index, patch);
  };
  const isCourtesy = (index) => staffMode && staffOpts[index]?.courtesy;
  const missingReason = map(products, (_, index) => index).some(
    (index) => isCourtesy(index) && !staffOpts[index]?.courtesyReason?.trim()
  );
  const { addOrderToTable } = useOrder();
  const { getTableByNumber } = useTable();
  const { tableNumber } = useParams();
  const history = useHistory();

  useEffect(() => {
    let totalTemp = 0;
    forEach(products, (product, index) => {
      if (isCourtesy(index)) return; // cortesía: no suma
      totalTemp +=
        getUnitPrice(product) * (product.quantity || 1) + getExtrasTotal(product);
    });
    setTotal(totalTemp.toFixed(2));
  }, [products, staffOpts]);

  const removeProduct = (index) => {
    removeProductCartApi(index);
    onReloadCart();
  };

  const createOrder = async () => {
    let idTable = idTableProp;
    if (!idTable) {
      const tableData = await getTableByNumber(tableNumber);
      idTable = tableData[0].id;
    }

    for await (const [index, product] of products.entries()) {
      const quantity = product.quantity || 1;
      const unitPrice = getUnitPrice(product);
      const extrasTotal = getExtrasTotal(product);
      const opts = staffMode ? staffOpts[index] || {} : {};

      for (let i = 0; i < quantity; i++) {
        // Los adicionales son un costo compartido (ej. una porción de arroz
        // para 2 platos), así que van una sola vez, en la primera fila —
        // las demás solo llevan el precio base del plato.
        const isFirst = i === 0;
        const rowPrice = isFirst ? unitPrice + extrasTotal : unitPrice;
        await addOrderToTable(
          idTable,
          product.id,
          staffMode ? opts.note?.trim() || "" : product.note,
          product.optionChoice,
          isFirst ? product.extras : [],
          opts.courtesy ? 0 : rowPrice,
          product.offerId,
          {
            takeaway: opts.takeaway,
            isCourtesy: opts.courtesy,
            courtesyReason: opts.courtesyReason?.trim(),
            courtesyValue: rowPrice.toFixed(2), // lo que costaba (control)
          }
        );
      }
    }

    cleanProductCartApi();
    history.push(redirectPath || `/client/${tableNumber}/orders`);
  };

  return (
    <div className="list-product-cart">
      {map(products, (product, index) => (
        <div key={index} className="list-product-cart__product">
          <div>
            {product.image && <Image src={product.image} avatar />}
            <div>
              {product.offerTitle && (
                <p className="list-product-cart__offer">
                  🎁 {product.offerTitle}
                </p>
              )}
              <span>
                {product.title}
                {product.quantity > 1 ? ` x${product.quantity}` : ""}
              </span>
              {product.optionChoice && (
                <p className="list-product-cart__note">
                  {product.optionChoice}
                </p>
              )}
              {map(product.extras, (extra) => (
                <p className="list-product-cart__note" key={extra.id}>
                  + {extra.label} x{extra.quantity} (S/ {extra.price})
                </p>
              ))}
              {!staffMode && product.note && (
                <p className="list-product-cart__note">
                  Nota: {product.note}
                </p>
              )}
            </div>
          </div>
          <span className={isCourtesy(index) ? "list-product-cart__courtesy-price" : ""}>
            {isCourtesy(index) && (
              <s>
                S/{" "}
                {(
                  getUnitPrice(product) * (product.quantity || 1) +
                  getExtrasTotal(product)
                ).toFixed(2)}
              </s>
            )}{" "}
            S/{" "}
            {isCourtesy(index)
              ? "0.00"
              : (
                  getUnitPrice(product) * (product.quantity || 1) +
                  getExtrasTotal(product)
                ).toFixed(2)}
          </span>
          <Icon name="close" onClick={() => removeProduct(index)} />
          {staffMode && (
            <div className="list-product-cart__staff">
              <Input
                size="small"
                className="list-product-cart__note-input"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                enterKeyHint="done"
                maxLength={200}
                placeholder="Nota para cocina, ej. sin ají"
                value={staffOpts[index]?.note || ""}
                onChange={(_, data) => setOpt(index, { note: data.value })}
              />
              <Checkbox
                toggle
                label="Para llevar"
                checked={!!staffOpts[index]?.takeaway}
                onChange={(_, data) => setOpt(index, { takeaway: data.checked })}
              />
              <Checkbox
                toggle
                label="Cortesía"
                checked={!!staffOpts[index]?.courtesy}
                onChange={(_, data) => setOpt(index, { courtesy: data.checked })}
              />
              {staffOpts[index]?.courtesy && (
                <Input
                  size="small"
                  placeholder="Motivo de la cortesía, ej. Cliente frecuente"
                  value={staffOpts[index]?.courtesyReason || ""}
                  list="courtesy-reasons"
                  onChange={(_, data) => setOpt(index, { courtesyReason: data.value })}
                />
              )}
            </div>
          )}
        </div>
      ))}

      {staffMode && (
        <datalist id="courtesy-reasons">
          {DISCOUNT_REASONS.filter((reason) => reason !== "Otro").map((reason) => (
            <option key={reason} value={reason} />
          ))}
        </datalist>
      )}

      {missingReason && (
        <p className="list-product-cart__warning">
          Indica el motivo de cada cortesía para poder realizar el pedido.
        </p>
      )}

      <Button primary fluid onClick={createOrder} disabled={missingReason}>
        Realizar pedido (S/ {total})
      </Button>
    </div>
  );
}

function getUnitPrice(product) {
  return product.unitPrice != null
    ? Number(product.unitPrice)
    : Number(product.price);
}

function getExtrasTotal(product) {
  return (product.extras || []).reduce(
    (acc, extra) => acc + Number(extra.price) * extra.quantity,
    0
  );
}
